import { format, parseISO } from 'date-fns';
import { Image } from 'lucide-react';

import noLogEntriesPng from '@/assets/illustrations/empty/no-log-entries.png';
import noLocationScoutsPng from '@/assets/illustrations/empty/no-location-scouts.png';

import { Button } from '@/components/common/Button';
import { TagChip } from '@/components/scouting/TagChip';
import { OperationalGuide } from '@/components/common/OperationalGuide';
import { formatTagLabel } from '@/utils/format-utils';
import { formatAnimalSummary } from '@/utils/scouting-utils';
import type { LogEntry } from '@/types/log-entry';

export interface LogTimelineProps {
  entries: LogEntry[];
  onEntryClick: (id: string) => void;
  onAddClick: () => void;
  /**
   * Controls which illustration/heading to show in the empty state.
   * - 'scout' (default): field-level log empty state
   * - 'location': waypoint-scoped view empty state
   */
  emptyVariant?: 'scout' | 'location';
}

export function LogTimeline({ entries, onEntryClick, onAddClick, emptyVariant = 'scout' }: LogTimelineProps) {
  const sorted = [...entries].sort((a, b) => b.date.localeCompare(a.date));

  // Group entries by date for display
  const groupedDates = sorted.reduce<string[]>((acc, entry) => {
    if (!acc.includes(entry.date)) acc.push(entry.date);
    return acc;
  }, []);

  return (
    <div className="flex flex-col gap-4" data-testid="log-timeline">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-display text-display-sm text-[var(--color-text-display)]">
          Scouting Log{entries.length > 0 && ` (${entries.length})`}
        </h2>
        <Button variant="ghost" onClick={onAddClick} data-testid="add-entry-button">
          + Add Entry
        </Button>
      </div>

      {sorted.length === 0 ? (
        <OperationalGuide
          illustration={emptyVariant === 'location' ? noLocationScoutsPng : noLogEntriesPng}
          heading={emptyVariant === 'location' ? 'No Scouts Logged Here' : 'Field Log is Empty'}
          subtext={
            emptyVariant === 'location'
              ? 'Log a scouting visit to start building a picture of this spot.'
              : 'Log a scout to start tracking movement patterns and atmospheric data.'
          }
          ctaLabel="Log Entry"
          onCtaClick={onAddClick}
          compact
          data-guide-id={emptyVariant === 'location' ? 'log-timeline-location-empty' : 'log-timeline-scout-empty'}
        />
      ) : (
        <div className="relative flex flex-col" data-testid="timeline-list">
          {/* Vertical spine */}
          <div
            className="absolute left-[5px] top-2 bottom-2 w-[2px] bg-bark-600"
            aria-hidden="true"
            data-testid="timeline-spine"
          />

          {groupedDates.map((date) => {
            const dateEntries = sorted.filter((e) => e.date === date);

            return (
              <div key={date} className="relative flex flex-col">
                {/* Date header — one per unique date */}
                <div className="flex items-center gap-3 mb-2 mt-4 first:mt-0">
                  <div
                    className="relative z-10 w-3 h-3 rounded-full bg-ember-500 ring-4 ring-bark-900 shrink-0"
                    data-testid="timeline-date-marker"
                    aria-hidden="true"
                  />
                  <span className="field-label pl-1">
                    {format(parseISO(date), 'MMM d, yyyy')}
                  </span>
                </div>

                {/* Entries for this date — each gets its own ember dot */}
                {dateEntries.map((entry) => {
                  const animalSummary = formatAnimalSummary(entry);
                  const truncatedNotes =
                    entry.notes.length > 100 ? `${entry.notes.slice(0, 100)}…` : entry.notes;

                  return (
                    <div key={entry.id} className="relative flex flex-col">
                      {/* Per-entry dot */}
                      <div className="flex items-start gap-3 mb-3">
                        <div
                          className="relative z-10 w-3 h-3 rounded-full bg-ember-500 ring-4 ring-bark-900 shrink-0 mt-2"
                          data-testid="timeline-entry-dot"
                          data-variant="ember"
                          aria-hidden="true"
                        />
                        <button
                          type="button"
                          onClick={() => onEntryClick(entry.id)}
                          className="flex-1 text-left border-[0.5px] border-bark-600 rounded-lg shadow-card p-3 flex flex-col gap-2 hover:border-[var(--color-border-subtle)] transition-colors"
                          data-testid={`entry-card-${entry.id}`}
                        >
                          {/* Time */}
                          {entry.time && (
                            <span className="text-body-xs text-[var(--color-text-muted)]">{entry.time}</span>
                          )}

                          {/* Notes excerpt */}
                          {truncatedNotes && (
                            <p className="text-body-sm text-[var(--color-text-primary)] leading-snug">{truncatedNotes}</p>
                          )}

                          {/* Tags */}
                          {entry.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {entry.tags.map((tag) => (
                                <TagChip
                                  key={tag}
                                  label={formatTagLabel(tag)}
                                  state="display"
                                />
                              ))}
                            </div>
                          )}

                          {/* Animal observation summary */}
                          {animalSummary && (
                            <p className="text-body-xs text-[var(--color-accent-hover)]">&#x1F98C; {animalSummary}</p>
                          )}

                          {/* Photo indicator */}
                          {(entry.photos?.length ?? 0) > 0 && (
                            <div className="flex items-center gap-1 text-body-xs text-[var(--color-text-muted)]" data-testid="photo-indicator">
                              <Image size={11} />
                              <span>{entry.photos!.length}</span>
                            </div>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
